import type { JSX } from "react";
import { useCallback, useEffect, useState } from "react";
import { artifactApi, SURFACE_LABEL, type ArtifactSummary, ArtifactError } from "../lib/artifactApi";
import { IconFile } from "../components/icons";
import "../styles/artifacts.css";

/**
 * 作品库 (Library) panel (FEATURE_ARTIFACTS · task #7 · Phase 7a, design mockup
 * ③). A per-user modal listing the artifacts the assistant GENERATED for this
 * user (documents produced in chat), managed independently from 我的资料 (which
 * holds user-UPLOADED materials): open in the side canvas, rename, delete. The
 * 「生成」badge marks them as generated (vs 上传/飞书). Read-only content in 7a;
 * promote-to-knowledge is Phase 7b. Mirrors the PersonalKbPanel modal idiom.
 */
export function ArtifactLibraryPanel({
  onClose,
  onOpen,
}: {
  onClose: () => void;
  /** Open one artifact in the side canvas (the parent also dismisses this panel). */
  onOpen: (artifactId: string) => void;
}): JSX.Element {
  const [artifacts, setArtifacts] = useState<ArtifactSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState("");

  const refresh = useCallback(async () => {
    setLoading(true);
    setArtifacts(await artifactApi.list());
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function onRemove(id: string): Promise<void> {
    setBusy(true);
    const ok = await artifactApi.remove(id);
    if (ok) setArtifacts((prev) => prev.filter((a) => a.id !== id));
    setBusy(false);
  }

  function startRename(a: ArtifactSummary): void {
    setEditingId(a.id);
    setEditTitle(a.title);
    setErr("");
  }

  async function commitRename(id: string): Promise<void> {
    const title = editTitle.trim();
    if (title.length === 0) {
      setEditingId(null);
      return;
    }
    setBusy(true);
    try {
      const updated = await artifactApi.rename(id, title);
      setArtifacts((prev) => prev.map((a) => (a.id === id ? { ...a, title: updated.title } : a)));
      setEditingId(null);
    } catch (e) {
      setErr(e instanceof ArtifactError ? e.message : "改名没成功，稍后再试。");
    }
    setBusy(false);
  }

  function renderCard(a: ArtifactSummary): JSX.Element {
    const version = a.versionCount > 1 ? ` · v${a.versionCount}` : "";
    const editing = editingId === a.id;
    return (
      <li className="artifact-tile" key={a.id} data-testid="artifact-tile">
        <button
          type="button"
          className="artifact-tile-open"
          aria-label={`打开 ${a.title}`}
          onClick={() => onOpen(a.id)}
        >
          <span className="artifact-tile-thumb" aria-hidden="true">
            <IconFile size={16} />
          </span>
        </button>
        <div className="artifact-tile-body">
          {editing ? (
            <input
              className="artifact-tile-edit"
              value={editTitle}
              autoFocus
              disabled={busy}
              data-testid="artifact-rename-input"
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void commitRename(a.id);
                else if (e.key === "Escape") setEditingId(null);
              }}
              onBlur={() => void commitRename(a.id)}
            />
          ) : (
            <button
              type="button"
              className="artifact-tile-title"
              onClick={() => onOpen(a.id)}
            >
              <span className="artifact-tile-name">{a.title}</span>
              <span className="artifact-badge artifact-badge-gen">生成</span>
            </button>
          )}
          <div className="artifact-tile-meta">
            {SURFACE_LABEL[a.surface]}
            {version} · {relTime(a.updatedAt)}
            {a.promoted && <span className="artifact-tile-promoted"> · 已入知识</span>}
          </div>
        </div>
        <div className="artifact-tile-acts">
          <button
            type="button"
            className="artifact-tile-act"
            disabled={busy}
            onClick={() => startRename(a)}
            data-testid="artifact-rename"
          >
            重命名
          </button>
          <button
            type="button"
            className="artifact-tile-act artifact-tile-del"
            disabled={busy}
            onClick={() => void onRemove(a.id)}
            data-testid="artifact-delete"
          >
            删除
          </button>
        </div>
      </li>
    );
  }

  return (
    <div className="pkb-modal-bg" onClick={onClose}>
      <div
        className="pkb-modal"
        role="dialog"
        aria-modal="true"
        aria-label="作品库"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="pkb-head">
          <h2>
            作品库
            {!loading && artifacts.length > 0 && (
              <span className="artifact-count"> · 你生成的产物 · {artifacts.length}</span>
            )}
          </h2>
          <button className="pkb-x" type="button" onClick={onClose} aria-label="关闭">
            ×
          </button>
        </div>
        <div className="pkb-body">
          <div className="pkb-reassure">
            这里是小环为你生成的文档。可以打开查看、重命名或删除，也能下载留存。
          </div>
          {err !== "" && (
            <div className="pkb-err" role="alert">
              {err}
            </div>
          )}
          {loading ? (
            <div className="pkb-note">加载中…</div>
          ) : artifacts.length === 0 ? (
            <div className="pkb-empty">还没有生成的文档。在对话里让小环整理一份，就会出现在这里。</div>
          ) : (
            <ul className="artifact-grid" data-testid="artifact-grid">
              {artifacts.map(renderCard)}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

/** Human-friendly age of a timestamp ("刚刚" / "3 天前" / a date). */
function relTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "未知时间";
  const secs = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return "刚刚";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  return new Date(iso).toLocaleDateString("zh-CN");
}
